/**
 * This service is similar to the `wootils`' `AppConfiguration`. It allows you to write, merge
 * and read a configuration dictionary on a really simple way.
 */
class AppConfiguration {
  /**
   * @param {AppUtils} appUtils To be able to access the configuration values using a object-like
   *                            path: `prop1.prop2.prop3`.
   */
  constructor(appUtils) {
    'inject';

    /**
     * A local reference for the `appUtils` service.
     * @type {AppUtils}
     * @access protected
     */
    this._appUtils = appUtils;
    /**
     * The dictionary where the configuration will be saved.
     * @type {Object}
     * @access protected
     */
    this._config = {};
  }
  /**
   * Access a configuration property or properties.
   * @param {string|Array} property The path to one or more configuration properties.
   * @return {*|Object} If a string is used as a parameter, the method will return the value of
   *                    that property alone; but if an Array is used, a dictionary with the
   *                    name/path as key will be returned instead.
   */
  get(property) {
    let result;
    if (Array.isArray(property)) {
      result = {};
      property.forEach((name) => {
        result[name] = this.get(name);
      });
    } else {
      result = this._appUtils.getPropertyWithPath(this.getConfig(), property);
    }

    return result;
  }
  /**
   * Get the whole configuration.
   * @return {Object}
   */
  getConfig() {
    return this._config;
  }
  /**
   * Set the new configuration.
   * @param {...Object} args If there's more than one argument, they'll all be merged.
   * @return {Object} The new configuration.
   */
  setConfig(...args) {
    let newConfig;
    if (args.length === 1) {
      [newConfig] = args;
    } else if (args.length > 1) {
      newConfig = Object.assign(...[{}, ...args]);
    } else {
      newConfig = {};
    }

    this._config = newConfig;
    return this.getConfig();
  }
}

export { AppConfiguration };
